import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import type { EmailSenderOption } from './use-email-sender-options'

/** Shown under the picker when some mailboxes are greyed out. */
export const EMAIL_SENDER_NOT_SHARED_HINT =
  "Greyed-out mailboxes aren't shared with you. Ask the owner or an admin to share one in Settings → Email."

/** Shown when a draft still points at a mailbox the user can no longer send as. */
export const EMAIL_SENDER_NOT_SHARED_MESSAGE = "That mailbox isn't shared with you. Choose another mailbox."

function senderLabel(option: EmailSenderOption): string {
  return option.displayName ? `${option.displayName} <${option.emailAddress}>` : option.emailAddress
}

interface EmailSenderSelectProps {
  value: string
  options: readonly EmailSenderOption[]
  disabled?: boolean
  /** On a reply, the mailbox the thread came in on. */
  cameInOnEmail?: string
  onChange: (value: string) => void
}

/**
 * The compose and reply "From" picker. Every active org mailbox is listed; the
 * ones the user may not send as are disabled, so they can see the mailbox
 * exists and ask for it to be shared. The server refuses those too.
 */
export function EmailSenderSelect({ value, options, disabled, cameInOnEmail, onChange }: EmailSenderSelectProps) {
  const someNotShared = options.some((option) => !option.canSend)
  return (
    <div className="grid gap-2">
      <Label htmlFor="from">
        From <span className="text-destructive">*</span>
      </Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id="from">
          <SelectValue placeholder="Select a mailbox" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id} disabled={!option.canSend}>
              {option.canSend ? senderLabel(option) : `${senderLabel(option)} · not shared with you`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {cameInOnEmail && <p className="text-xs text-muted-foreground">Came in on {cameInOnEmail}</p>}
      {someNotShared && <p className="text-xs text-muted-foreground">{EMAIL_SENDER_NOT_SHARED_HINT}</p>}
    </div>
  )
}
